// reducer/banLinkageSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isWeb } from '../storage';

export interface BanInfo {
  ban: string;
  accountId: string;
  customerId: string;
  accountNumber?: string;
  accountOpenedDate?: string;
  tn?: string[];
  otpPhones?: string[];
  autoValidation?: boolean;
}

export interface BANLinkageState {
  loading: boolean;
  banDetails: any;
  currentBan: BanInfo | null;
  banList: BanInfo[];
  selectedBan: BanInfo | null;
  selectedTN: string | null;
  otpSent: boolean;
  otpVerified: boolean;
  linkingStatus: boolean | null;
  isIgniteAccount: boolean | null;
  linkScreenIndicator?: string;
  rciAccounts?: any[];
  rciAccountStatus?: any;
  rciAccountDetails?: any;
  rewardAccountDetails?: any;
  autoValidation?: boolean;
  detail?: string;
  status?: string;
  title?: string;
  nextLinkAttemptDate?: string;
  previousConnectionAttemptDate?: string;
  previousConnectionStatus?: string;
  error: string | null;
}

const initialState: BANLinkageState = {
  loading: false,
  banDetails: null,
  currentBan: null,
  banList: [],
  selectedBan: null,
  selectedTN: null,
  otpSent: false,
  otpVerified: false,
  linkingStatus: null,
  isIgniteAccount: null,
  linkScreenIndicator: undefined,
  rciAccountStatus: undefined,
  rciAccountDetails: undefined,
  rewardAccountDetails: undefined,
  detail: undefined,
  status: undefined,
  title: undefined,
  error: null,
};

/**
 * Helper to check if an error is about "already registered" account
 * @param detail - Error detail string that might be a JSON string
 * @returns boolean indicating if this is a "Ban already registered" error
 */
const isAlreadyRegisteredError = (detail: string) => {
  if (!detail) return false;

  // First, check if it's a plain string with the error
  if (
    typeof detail === 'string' &&
    detail.includes('BNARG') &&
    detail.includes('Ban already registered')
  ) {
    return true;
  }

  // Then try to parse as JSON for nested structure
  try {
    const parsedDetail = JSON.parse(detail);
    if (
      parsedDetail.meta &&
      parsedDetail.meta.details &&
      parsedDetail.meta.details.length > 0
    ) {
      const detailsStr = parsedDetail.meta.details[0];
      return (
        detailsStr.includes('BNARG') &&
        detailsStr.includes('Ban already registered')
      );
    }
  } catch (e) {
    // Not parseable JSON, but we already checked plain string above
  }
  return false;
};

const banLinkageSlice = createSlice({
  name: 'banLinkage',
  initialState,
  reducers: {
    // Get BAN Details
    getBanDetailsRequest(
      state,
      action: PayloadAction<{
        ban: string;
        isRefresh?: boolean;
      }>
    ) {
      state.loading = true;
      state.error = null;
    },
    getBanDetailsSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.banDetails = action.payload;
      state.error = null;
    },
    getBanDetailsFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    fetchRCIAccountStatusRequest(
      state,
      action: PayloadAction<{
        accountId?: string;
      }>
    ) {
      state.loading = true;
      state.error = null;
    },
    fetchRCIAccountStatusSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.rciAccountStatus = action.payload;
      state.error = null;
    },
    fetchRCIAccountStatusFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    fetchRCIAccountRequest(
      state,
      action: PayloadAction<{
        accountId?: string;
      }>
    ) {
      state.loading = true;
      state.error = null;
    },
    fetchRCIAccountSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.rciAccountDetails = action.payload;
      state.error = null;
    },
    fetchRCIAccountFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    fetchRewardAccountRequest(
      state,
      action: PayloadAction<{
        user_id?: string;
      }>
    ) {
      state.loading = true;
      state.error = null;
    },
    fetchRewardAccountSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.rewardAccountDetails = action.payload;
      state.error = null;
    },
    fetchRewardAccountFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    // Initiate Link Card
    initiateLinkCardRequest(
      state,
      action: PayloadAction<{
        appCode: string;
        accountId: string;
        customerId: string;
      }>
    ) {
      state.loading = true;
      state.error = null;
      // Reset previous state values
      state.detail = undefined;
      state.status = undefined;
      state.title = undefined;
      state.linkScreenIndicator = undefined;
    },
    initiateLinkCardSuccess(state, action: PayloadAction<any>) {
      state.loading = false;

      const {
        linkScreenIndicator,
        rciAccounts,
        autoValidation,
        detail,
        status,
        title,
        banList,
        ban,
        isSingleUnit,
      } = action.payload;

      if (isSingleUnit || (banList && banList.length === 1)) {
        state.currentBan = ban || banList?.[0];
      } else if (banList) {
        state.banList = banList;
      }

      state.linkScreenIndicator = linkScreenIndicator;
      state.rciAccounts = rciAccounts;
      state.autoValidation = autoValidation;
      state.detail = detail;
      state.status = status;
      state.title = title;
      state.error = null;
    },
    initiateLinkCardFailed(
      state,
      action: PayloadAction<{ error: string; detail?: string }>
    ) {
      state.loading = false;
      state.error = action.payload.error;

      if (action.payload.detail) {
        state.detail = action.payload.detail;

        if (isAlreadyRegisteredError(action.payload.detail)) {
          state.linkScreenIndicator = 'LINKED_ALREADY';
        }
      }
    },

    validateSelectedAccountRequest(
      state,
      action: PayloadAction<{
        ban: string;
        accountId: string;
        customerId: string;
        rciAccount?: any;
      }>
    ) {
      state.loading = true;
      state.error = null;

      state.detail = undefined;
      state.status = undefined;
      state.title = undefined;
      state.linkScreenIndicator = '';
    },
    validateSelectedAccountSuccess(
      state,
      action: PayloadAction<{
        linkScreenIndicator?: string;
        rciAccounts?: any[];
        autoValidation?: boolean;
        detail?: string;
        status?: string;
        title?: string;
        banList?: BanInfo[];
        otpPhones?: string[];
        selectedBan?: BanInfo;
        ban?: BanInfo;
      }>
    ) {
      state.loading = false;

      const {
        linkScreenIndicator = state.linkScreenIndicator,
        rciAccounts = state.rciAccounts,
        autoValidation = state.autoValidation,
        detail = state.detail,
        status = state.status,
        title = state.title,
        banList = state.banList,
        //otpPhones = state.otpPhones,
        selectedBan,
        ban,
      } = action.payload;

      state.linkScreenIndicator = linkScreenIndicator;
      state.rciAccounts = rciAccounts;
      state.autoValidation = autoValidation;
      state.detail = detail;
      state.status = status;
      state.title = title;
      state.banList = banList;
      //state.otpPhones = otpPhones;

      if (selectedBan) {
        state.selectedBan = selectedBan;
      }
      if (ban) {
        state.selectedBan = { ...state.selectedBan, ...ban };
      }

      state.error = null;
    },

    validateSelectedAccountFailed(
      state,
      action: PayloadAction<{ error: string; detail?: string }>
    ) {
      state.loading = false;
      state.error = action.payload.error;

      // Store detail information
      if (action.payload.detail) {
        state.detail = action.payload.detail;

        // Check if this is a "Ban already registered" error
        if (isAlreadyRegisteredError(action.payload.detail)) {
          state.linkScreenIndicator = 'LINKED_ALREADY';
        }
      }
    },

    // Link Selected Account
    linkSelectedAccountRequest(
      state,
      action: PayloadAction<{
        ban: string;
        accountId: string;
        customerId: string;
      }>
    ) {
      state.loading = true;
      state.error = null;
    },
    linkSelectedAccountSuccess(state, action: PayloadAction<any>) {
      console.log(
        '[banLinkageSlice] ===== linkSelectedAccountSuccess called ====='
      );
      state.loading = false;

      const {
        linkingStatus,
        isIgniteAccount,
        detail,
        status,
        title,
        linkScreenIndicator = state.linkScreenIndicator,
        nextLinkAttemptDate,
        previousConnectionAttemptDate,
        previousConnectionStatus,
        error,
      } = action.payload;

      console.log('[banLinkageSlice] action.payload:', action.payload);
      console.log('[banLinkageSlice] status:', status);
      console.log('[banLinkageSlice] detail:', detail);
      console.log(
        '[banLinkageSlice] linkScreenIndicator from payload:',
        linkScreenIndicator
      );

      state.linkingStatus = linkingStatus;
      state.isIgniteAccount = isIgniteAccount;
      state.detail = detail;
      state.status = status;
      state.title = title;
      state.error = error?.errCode;

      // Check if this is a 409 error (Ban already registered)
      if ((status === '409' || status === 409) && detail) {
        console.log('[banLinkageSlice] Detected 409 status with detail');
        if (isAlreadyRegisteredError(detail)) {
          console.log(
            '[banLinkageSlice] Confirmed "Ban already registered" error'
          );
          state.linkScreenIndicator = 'LINKED_ALREADY';
        } else {
          console.log('[banLinkageSlice] 409 but not "Ban already registered"');
          if (isWeb) {
            state.linkScreenIndicator = 'LINKED_ALREADY';
          } else {
            state.linkScreenIndicator = 'TEMP_UNAVAILABLE';
          }
        }
      } else {
        // Use the linkScreenIndicator from payload or keep existing
        state.linkScreenIndicator = linkScreenIndicator;
      }

      console.log(
        '[banLinkageSlice] Final state.linkScreenIndicator:',
        state.linkScreenIndicator
      );
      console.log(
        '[banLinkageSlice] ===== linkSelectedAccountSuccess completed ====='
      );

      // state.nextLinkAttemptDate = nextLinkAttemptDate;
      // state.previousConnectionAttemptDate = previousConnectionAttemptDate;
      // state.previousConnectionStatus = previousConnectionStatus;

      // state.error = error.statusCode || null;
    },
    linkSelectedAccountFailed(
      state,
      action: PayloadAction<{
        error: string;
        detail?: string;
        status?: string;
        title?: string;
      }>
    ) {
      console.log(
        '[banLinkageSlice] ===== linkSelectedAccountFailed called ====='
      );
      console.log('[banLinkageSlice] action.payload:', action.payload);
      state.loading = false;
      state.error = action.payload.error;
      state.status = action.payload.status;
      state.title = action.payload.title;

      if (action.payload.detail) {
        state.detail = action.payload.detail;
        console.log('[banLinkageSlice] detail present:', action.payload.detail);

        if (isAlreadyRegisteredError(action.payload.detail)) {
          console.log(
            '[banLinkageSlice] Detected "Ban already registered" error'
          );
          state.linkScreenIndicator = 'LINKED_ALREADY';
          console.log(
            '[banLinkageSlice] Set linkScreenIndicator to: LINKED_ALREADY'
          );
        } else {
          console.log('[banLinkageSlice] Not a "Ban already registered" error');
          state.linkScreenIndicator = 'TEMP_UNAVAILABLE';
          console.log(
            '[banLinkageSlice] Set linkScreenIndicator to: LINK_FAILED'
          );
        }
      }
      console.log(
        '[banLinkageSlice] ===== linkSelectedAccountFailed completed ====='
      );
    },

    // Generate OTP
    generateOtpRequest(
      state,
      action: PayloadAction<{
        accountId: string;
        mobileNumber: string;
        language: string;
        recaptchaToken: string;
      }>
    ) {
      state.loading = true;
      state.error = null;
      state.otpSent = false;
    },
    generateOtpSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.otpSent = true;
      state.error = null;
      state.linkScreenIndicator = 'LINK_OTP_VERIFICATION_SCREEN';
    },
    generateOtpFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.otpSent = false;
      state.error = action.payload;
    },

    // Submit OTP
    submitOtpRequest(
      state,
      action: PayloadAction<{
        accountId: string;
        mobileNumber: string;
        otp: string;
        recaptchaToken: string;
      }>
    ) {
      state.loading = true;
      state.error = null;
    },
    submitOtpSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.otpVerified = true;
      state.error = null;

      state.linkScreenIndicator = 'LINK_ACCOUNT_CONFIRMATION';
    },
    submitOtpFailed(state, action: PayloadAction<string>) {
      console.log('[banLinkageSlice] ===== submitOtpFailed called =====');
      console.log('[banLinkageSlice] Payload:', action.payload);
      console.log('[banLinkageSlice] Payload type:', typeof action.payload);
      if (typeof action.payload === 'object') {
        console.log('[banLinkageSlice] Payload stringified:', JSON.stringify(action.payload));
      }
      state.loading = false;
      state.error = action.payload;
      console.log('[banLinkageSlice] Error set in state:', state.error);
      console.log('[banLinkageSlice] ===== submitOtpFailed completed =====');
    },
    
    clearOtpError(state) {
      console.log('[banLinkageSlice] ===== clearOtpError called =====');
      console.log('[banLinkageSlice] Previous error:', state.error);
      state.error = null;
      console.log('[banLinkageSlice] Error cleared - new value:', state.error);
      console.log('[banLinkageSlice] ===== clearOtpError completed =====');
    },

    // Unlink Account
    unlinkAccountRequest(
      state,
      action: PayloadAction<{
        ban: string;
        accountId: string;
        customerId: string;
      }>
    ) {
      state.loading = true;
      state.error = null;
    },
    unlinkAccountSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.linkingStatus = true;
      state.error = null;
    },
    unlinkAccountFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.linkingStatus = false;
      state.error = action.payload;
    },

    // Select BAN (for multi-unit)
    selectBan(state, action: PayloadAction<BanInfo>) {
      state.selectedBan = action.payload;
    },

    // Select TN (for OTP flow)
    selectTN(state, action: PayloadAction<string>) {
      state.selectedTN = action.payload;
    },

    resetLinkScreenIndicator(state) {
      console.log(
        '[banLinkageSlice] ===== resetLinkScreenIndicator called ====='
      );
      console.log(
        '[banLinkageSlice] Previous linkScreenIndicator:',
        state.linkScreenIndicator
      );
      console.trace(
        '[banLinkageSlice] Stack trace for resetLinkScreenIndicator'
      );
      state.linkScreenIndicator = undefined;
      console.log('[banLinkageSlice] linkScreenIndicator reset to undefined');
    },

    setLinkScreenIndicator(state, action: PayloadAction<string>) {
      state.linkScreenIndicator = action.payload;
    },

    resetBanLinkage(state) {
      return initialState;
    },
  },
});

export const {
  getBanDetailsRequest,
  getBanDetailsSuccess,
  getBanDetailsFailed,
  fetchRCIAccountStatusRequest,
  fetchRCIAccountStatusSuccess,
  fetchRCIAccountStatusFailed,
  fetchRCIAccountRequest,
  fetchRCIAccountSuccess,
  fetchRCIAccountFailed,
  fetchRewardAccountRequest,
  fetchRewardAccountSuccess,
  fetchRewardAccountFailed,
  initiateLinkCardRequest,
  initiateLinkCardSuccess,
  initiateLinkCardFailed,
  validateSelectedAccountRequest,
  validateSelectedAccountSuccess,
  validateSelectedAccountFailed,
  linkSelectedAccountRequest,
  linkSelectedAccountSuccess,
  linkSelectedAccountFailed,
  generateOtpRequest,
  generateOtpSuccess,
  generateOtpFailed,
  submitOtpRequest,
  submitOtpSuccess,
  submitOtpFailed,
  clearOtpError,
  unlinkAccountRequest,
  unlinkAccountSuccess,
  unlinkAccountFailed,
  selectBan,
  selectTN,
  setLinkScreenIndicator,
  resetLinkScreenIndicator,
  resetBanLinkage,
} = banLinkageSlice.actions;

export default banLinkageSlice.reducer;
